import { useMemo } from 'react'

import { useQueries } from '@tanstack/react-query'

import { createColDefs } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { useResizableState } from '@fortem-monorepo/react-hooks'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'

import { Page } from '@/components/Page'
import { PanelToggle } from '@/pages/operations/mappings/page'

const datahandlerColDefs = createColDefs([
  { field: 'id', hide: true },
  { field: 'user_id', hide: true },
  { field: 'full_name', headerName: 'User' },
  { field: 'upload_datetime_utc', headerName: 'Date' },
  { field: 'schema' },
  { field: 'table' },
  { field: 'command' },
  { field: 'source' },
  { field: 'is_internal', hide: true },
  { field: 'date', hide: true },
  {
    headerName: 'Details',
    groupId: 'details-group',
    children: [
      { field: 'hostname' },
      { field: 'ip', columnGroupShow: 'open' },
      { field: 'details', headerName: 'Query', columnGroupShow: 'open', width: 1000 },
    ],
  },
])

const steppedTasksColDefs = createColDefs([
  { field: 'id', hide: true },
  { field: 'run_id', hide: true, rowGroup: true },
  { field: 'full_name' },
  { field: 'upload_datetime_utc', sort: 'desc' },
  { field: 'key' },
  { field: 'step' },
  { field: 'crashed' },
  { field: 'output' },
  { field: 'user_id', hide: true },
  { field: 'is_internal', hide: true },
])

export interface LogsPageProps {}

export const LogsPage: React.FC<LogsPageProps> = ({}) => {
  const [dhState, stState] = useResizableState(2)

  const api = useApi()

  const queries = useQueries({
    queries: [
      {
        queryKey: ['logs', 'datahandler-queries'],
        queryFn: () => api.logs.getDatahandlerQueries(),
      },
      {
        queryKey: ['logs', 'stepped-tasks'],
        queryFn: () => api.logs.getSteppedTasks(),
      },
    ],
  })

  const [datahandlerQuery, steppedTasksQuery] = queries

  const dataHandlerData = useMemo(() => {
    return datahandlerQuery.data ?? []
  }, [datahandlerQuery.data])

  const steppedTasksData = useMemo(() => {
    return (steppedTasksQuery.data ?? []).sort(
      (a, b) =>
        new Date(b.upload_datetime_utc).getTime() - new Date(a.upload_datetime_utc).getTime(),
    )
  }, [steppedTasksQuery.data])

  return (
    <Page metadata={{ title: 'Logs' }}>
      <HeaderConfigBar noPadding>
        <div className={'flex items-center'}>
          <div className={'h-full w-px bg-border'} />
          <PanelToggle open={dhState.open} onClick={() => dhState.toggle()}>
            DATAHANDLER QUERIES
          </PanelToggle>
          <div className={'h-full w-px bg-border'} />
          <PanelToggle open={stState.open} onClick={() => stState.toggle()}>
            STEPPED TASKS
          </PanelToggle>
          <div className={'h-full w-px bg-border'} />
        </div>
      </HeaderConfigBar>
      <ResizablePanelGroup direction={'horizontal'} autoSaveId={'conditional'}>
        {dhState.open && (
          <ResizablePanel order={1} id="panel--datahandler-queries">
            <Widget exposed className={'h-full'}>
              <WidgetHeaderWithQuery query={datahandlerQuery} title={'Datahandler Queries'} />
              <WidgetContent loading={datahandlerQuery.isLoading}>
                <CustomAgGrid
                  domLayout={'normal'}
                  rowData={dataHandlerData}
                  columnDefs={datahandlerColDefs}
                />
              </WidgetContent>
            </Widget>
          </ResizablePanel>
        )}
        {stState.open && (
          <>
            {stState.open && dhState.open && <ResizableHandle />}
            <ResizablePanel order={2} id="panel--stepped-tasks">
              <Widget exposed className={'h-full'}>
                <WidgetHeaderWithQuery query={steppedTasksQuery} title="Stepped Tasks" />
                <WidgetContent loading={steppedTasksQuery?.isLoading}>
                  <CustomAgGrid
                    domLayout={'normal'}
                    rowData={steppedTasksData}
                    columnDefs={steppedTasksColDefs}
                  />
                </WidgetContent>
              </Widget>
            </ResizablePanel>
          </>
        )}
      </ResizablePanelGroup>
    </Page>
  )
}
